import { useToast } from "@chakra-ui/react";
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from "chart.js";
import { useEffect, useMemo, useState } from "react";
import { Bar, Pie } from "react-chartjs-2";
import { useSearchParams } from "react-router-dom";
import { useTable } from "react-table";
import { RequirementGuard } from "../../components/component-require-role";
import Index from "../../components/ui/DashboardIndex";
import EmptyList from "../../components/ui/EmptyList";
import { FieldLabel } from "../../components/ui/form";
import { Loading } from "../../components/ui/loading";
import { ResourceLoadingError } from "../../components/ui/resource-loading-error";
import Workspace from "../../components/workspace";
import authService from "../../services/auth-service";
import dashboardService from "../../services/dashboard-service";
import { useBranches } from "../../state/branchListContext";
import "../../styles/dashboard.css";

ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Title);

const Dashboard = () => {
   const currentDashboardBranch = useSearchParams()[0].get("branch") || null;

   const [isLoading, setIsLoading] = useState(true);
   const [indexes, setIndexes] = useState([]);
   const [tables, setTables] = useState({ celebrations: [] });
   const [charts, setCharts] = useState({});
   const [fetchingError] = useState(null);
   const { userCanSwitchBranch } = useBranches();
   const toast = useToast();

   // const celebrationsTableData = useMemo(() => tables.celebrations, [tables.celebrations]);
   const celebrationsTableData = useMemo(() => tables?.celebrations || [], [tables?.celebrations]);
   const celebrationsTableColumns = useMemo(
      () => [
         {
            Header: "Name",
            accessor: "fullName", // accessor is the "key" in the data
         },
         {
            Header: "Celebration",
            accessor: "celebration",
         },
         {
            Header: "Date",
            accessor: "date",
         },
         {
            Header: "Phone",
            accessor: "phoneNo",
         },
         {
            Header: "Day",
            accessor: "day",
         },
      ],
      []
   );
   const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
      columns: celebrationsTableColumns,
      data: celebrationsTableData,
   });

   const genderDistributionData = {
      labels: charts?.genderDistribution?.data?.gender,
      datasets: [
         {
            data: charts?.genderDistribution?.data?.count,
            borderWidth: 3,
            backgroundColor: ["rgb(255, 99, 132)", "rgb(54, 162, 235)", "rgb(255, 206, 86)"],
         },
      ],
   };

   const offeringMonthOnMonthData = {
      labels: charts?.offeringMonthOnMonth?.data?.month,
      datasets: [{ data: charts?.offeringMonthOnMonth?.data?.amount, backgroundColor: "rgb(30, 99, 212)" }],
   };

   useEffect(() => {
      const getDashboardIndicators = async () => {
         try {
            const fetchResult = await dashboardService.getDashboard(currentDashboardBranch || "global");
            setIndexes(fetchResult.data?.metrics);
            setCharts(fetchResult.data?.charts);
            setTables(fetchResult.data?.tables);
         } catch (error) {
            toast({ status: "error", description: error.message });
         } finally {
            setIsLoading(false);
         }
      };
      getDashboardIndicators();
      return () => {
         setIndexes([]);
         setIsLoading(true);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentDashboardBranch]);

   if (isLoading) return <Loading />;
   if (fetchingError) {
      return <ResourceLoadingError error="Looks like your dashboard is not loading" showButton={false} />;
   }

   return (
      <>
         <Workspace.TopBar heading="Dashboard" />

         <div className="mb-4">
            <RequirementGuard condition={userCanSwitchBranch()}>
               <DashboardViewSwitch toast={toast} />
            </RequirementGuard>
         </div>

         <div className="flex flex-col gap-y-8 mb-8">
            <div className="indexes grid gap-3 grid-flow-dense grid-cols-[repeat(auto-fill, minmax(230px, 1fr))]">
               {indexes && indexes?.length ? indexes?.map((index, key) => <Index index={index} key={key} />) : null}
            </div>

            <RequirementGuard condition={tables?.celebrations}>
               <div className="bg-white rounded-2xl">
                  <h2 className="text-xl px-3 md:px-6 py-4 mb-0 border-secondary-local">Celebrations</h2>
                  <div className="table celebrants p-0 w-full">
                     {tables?.celebrations?.length ? (
                        <table className="grid text-[0.8rem] mb-8" {...getTableProps()}>
                           <thead>
                              {headerGroups?.map((headerGroup, index) => (
                                 <tr
                                    className="bg-[--clr-secondary] text-white flex md:px-4"
                                    {...headerGroup.getHeaderGroupProps()}
                                    key={index}>
                                    {headerGroup.headers?.map((column) => (
                                       <th
                                          className="text-left py-4 px-2 first:w-full last:w-1/12 [&:nth-child(2)]:w-3/12 [&:nth-child(4)]:w-3/12 [&:nth-child(3)]:w-3/12"
                                          {...column.getHeaderProps()}
                                          key={column.id}>
                                          {column.render("Header")}
                                       </th>
                                    ))}
                                 </tr>
                              ))}
                           </thead>
                           <tbody {...getTableBodyProps()}>
                              {rows?.map((row) => {
                                 prepareRow(row);
                                 return (
                                    <tr className="flex hover:bg-[--bg-clr] md:px-4" {...row.getRowProps()} key={row.id}>
                                       {row.cells?.map((cell) => (
                                          <td
                                             className="first:font-semibold text-left py-4 px-2 first:w-full last:w-1/12 [&:nth-child(2)]:w-3/12 [&:nth-child(4)]:w-3/12 [&:nth-child(3)]:w-3/12 [&:nth-child(3)]:bg-[--bg-clr]"
                                             {...cell.getCellProps()}
                                             key={cell.value}>
                                             {cell.render("Cell")}
                                          </td>
                                       ))}
                                    </tr>
                                 );
                              })}
                           </tbody>
                        </table>
                     ) : (
                        <div className="message-default p-5 text-center">
                           <EmptyList message="No celebration record found" />
                        </div>
                     )}
                  </div>
               </div>
            </RequirementGuard>
            <RequirementGuard condition={charts?.offeringMonthOnMonth}>
               <ChartCard className="rounded-2xl">
                  <h2 className="lg:text-xl text-center mb-8">Offering Month-on-month</h2>
                  <Bar options={{ responsive: true, plugins: { legend: { display: false } } }} data={offeringMonthOnMonthData} />
               </ChartCard>
            </RequirementGuard>

            <div className="flex flex-wrap justify-between">
               <RequirementGuard condition={charts?.genderDistribution}>
                  <ChartCard className="rounded-2xl">
                     <h2 className="text-xl text-center mb-8">Gender Distribution</h2>
                     <Pie data={genderDistributionData} />
                  </ChartCard>
               </RequirementGuard>
            </div>
         </div>
      </>
   );
};
export default Dashboard;

function ChartCard({ children, className = "" }) {
   return <div className={`bg-white border-secondary-local p-4 lg:p-12 ${className}`}>{children}</div>;
}

const DashboardViewSwitch = ({ toast }) => {
   const [searchParams, setSearchParams] = useSearchParams();
   const requestBranch = authService.getRequestBranchFromCookie();

   return (
      <div className="grid lg:justify-items-end">
         <div className="bg-white py-3 px-4 rounded-2xl">
            <FieldLabel htmlFor="dashboardView">Select dashboard view</FieldLabel>
            <select
               name="dashboardView"
               className="form-select | border border-[#ced4da] rounded-lg overflow-hidden text-[0.8rem] w-full px-2 py-[0.3rem]"
               value={searchParams.has("branch") ? "branch" : "global"}
               onChange={(e) => {
                  e.target.value === "global" ? searchParams.delete("branch") : searchParams.set("branch", requestBranch);

                  setSearchParams(searchParams);
                  toast({ status: "info", description: "Dashboard view has been changed" });
               }}>
               <option value="global">Global</option>
               <option value="branch">Branch</option>
            </select>
         </div>
      </div>
   );
};
